<template>
  <div class="pb-2 pl-px">
    <div class="flex flex-col mt-4">
      <label class="font-bold">Context Path</label>
      <base-input v-model="contextPath"
                  :color="focusColor"
                  :class="{'border-focus-error focus:border-focus-error': isVariableKeyError}"
                  class="w-full h-10 mt-2"
                  variant="editor-soft" />
      <span class="mt-1"
            :class="isVariableKeyError ? 'text-focus-error': 'text-gray'"
      >
        {{ isVariableKeyError ? errorDescription : contextPathExample }}
      </span>
    </div>

    <div class="flex flex-col mt-4">
      <label class="font-bold">Condition Value</label>
      <div class="h-10 flex mt-2">
        <base-select :value="selectedExpressionType"
                     :options="expressionTypeOptions"
                     class="w-40"
                     @input="updateExpressionType"
        />
        <base-input v-model="payload"
                    :color="focusColor"
                    ref="conditionValue"
                    class="w-full ml-2"
                    :class="{'border-focus-error focus:border-focus-error': isPayloadError}"
                    variant="editor-soft" />
      </div>
      <div class="flex mt-1">
        <span v-if="displayRegexHint" class="text-gray">
          Test your regex <a href="https://regex101.com/" target="_blank">here</a>.
        </span>
        <span v-if="isPayloadError" class="text-focus-error ml-auto">
          {{ isRegexInvalidError ? 'Invalid regex' : errorDescription }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import tailwind from '../../../../../tailwind.config'
import conditionUtils from '@/utils/condition.utils'
import { CONDITION_CASES } from '@/store/constants'
import { EXPRESSION_TYPES } from '@/utils/bot-json.constants'
import previewModule from '@/store/modules/preview.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'

const EXPRESSION_TYPES_LABEL_MAP = {
  [EXPRESSION_TYPES.KEY_EQUALS]: 'Equals',
  [EXPRESSION_TYPES.KEY_REGEX]: 'Regex'
}

const LABEL_EXPRESSION_TYPES_MAP = {
  'Equals' : EXPRESSION_TYPES.KEY_EQUALS,
  'Regex' : EXPRESSION_TYPES.KEY_REGEX
}

export default {
  name: 'ConditionCaseContext',

  props: {
    expressionType: {
      type: String,
      required: true
    },
    value: {
      type: Object,
      required: true
    }
  },

  watch: {
    contextPath(contextPath) {
      this.updateCondition({ ...this.value, key: contextPath })
      if (this.isVariableKeyError) this.error = null
    },

    payload(payload) {
      const payloadKey = this.getContextPayloadKey()
      this.updateCondition({ key: this.contextPath, [payloadKey]: payload })
      if (this.isPayloadError) this.error = null
    }
  },

  data() {
    return {
      contextPath: this.value.key,
      payload: '',
      payloadBackups: {
        equals: '200',
        regex: '^hello$'
      }
    }
  },

  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    isVariableKeyError() {
      return chatBubbleModule.isErrorBubbleFocused() &&
        this.errorDescription?.startsWith('Variable') &&
        (this.error?.path === 'keyEquals.key' || this.error?.path === 'keyRegex.key')
    },

    isVariablePayloadError() {
      return chatBubbleModule.isErrorBubbleFocused() &&
        this.errorDescription?.startsWith('Variable') &&
        (this.error?.path === 'keyEquals.equals' || this.error?.path === 'keyRegex.regex')
    },

    isPayloadError() {
      return chatBubbleModule.isErrorBubbleFocused() &&
        (this.isVariablePayloadError || this.isRegexInvalidError ||
        this.errorDescription === 'Equals is not allowed to be empty!'
        )
    },

    isRegexInvalidError() {
      return (
        chatBubbleModule.isErrorBubbleFocused() &&
        (this.errorDescription?.endsWith('is not a valid regular expression.') ||
        this.errorDescription === 'Regex is not valid!')
      )
    },

    focusColor() {
      return tailwind.theme.colors.focus
    },

    contextPathExample() {
      return '${context.' + this.contextPath + '}'
    },

    selectedExpressionType() {
      return EXPRESSION_TYPES_LABEL_MAP[this.expressionType]
    },

    expressionTypeOptions() {
      return Object.values(EXPRESSION_TYPES_LABEL_MAP)
    },

    displayRegexHint() {
      return this.selectedExpressionType === EXPRESSION_TYPES_LABEL_MAP[EXPRESSION_TYPES.KEY_REGEX]
    }
  },

  methods: {
    
    getContextPayloadKey(expressionType = this.expressionType) {
      return conditionUtils.getContextPayloadKey(expressionType)
    },

    backupPayload() {
      if (this.expressionType === EXPRESSION_TYPES.KEY_EQUALS) {
        this.payloadBackups.equals = this.payload
        return
      }

      this.payloadBackups.regex = this.payload
    },

    updateExpressionType(e) {
      this.backupPayload()

      const updatedExpressionType = LABEL_EXPRESSION_TYPES_MAP[e]
      const payloadKey = this.getContextPayloadKey(updatedExpressionType)
      this.payload = this.payloadBackups[payloadKey]
      this.updateCondition({ key: this.contextPath, [payloadKey]: this.payload }, updatedExpressionType)
    },

    updateCondition(expressionValue, expressionType = this.expressionType) {
      const condition = conditionUtils.createConditionJson(CONDITION_CASES.CONTEXT, expressionValue, expressionType)
      this.$emit('input', condition)
    }
  },

  mounted() {
    const payloadKey = this.getContextPayloadKey()
    this.payload = this.value[payloadKey]
    if (this.isPayloadError) this.$refs.conditionValue.focus()
  }
}
</script>
